/**
 * When a deal's disbursement is locked, mirroring `services/disbursement_lock.py`.
 *
 * A locked deal refuses `reset-elements` and `reset-net-in-hand` for the money elements and
 * `execute-disbursement`, so the screen withholds what the server would 409. The status list
 * is held equal to the backend's by `backend/tests/test_disbursement_lock_frontend_mirror.py`,
 * which parses the array literal below: keep it a plain array of string literals.
 */
export const DISBURSEMENT_IN_FLIGHT_STATUSES = [
  "sending",
  "skipped_not_configured",
  "send_unknown",
  "manual_capay_pending",
];

/**
 * True when the deal is funded, its disbursement is in flight, or a GoCardless or Capay
 * payment exists for it. A Capay id is stored whenever one is known, including when the
 * send then failed or its outcome is unknown, so it locks an unfunded deal on its own.
 */
export function disbursementIsLocked(application) {
  return (
    !!application &&
    (!!application.disbursement_confirmed ||
      !!application.funded_at ||
      !!application.disbursement_gc_payment_id ||
      !!application.disbursement_capay_payment_id ||
      DISBURSEMENT_IN_FLIGHT_STATUSES.includes(application.disbursement_gc_send_status))
  );
}

/**
 * The in-flight statuses written with no rail call: staff were told to pay by hand, so
 * releasing one needs the Admin to confirm that no manual transfer was made. Mirrors
 * `UNPINNED_STATUSES` in `services/disbursement_lock.py`, held equal by
 * `backend/tests/test_disbursement_lock_frontend_mirror.py`.
 */
export const MANUAL_TRANSFER_STATUSES = ["skipped_not_configured", "manual_capay_pending"];

/** The statuses where a rail call may have been made, so a payment may exist unseen. */
const RAIL_CALL_STATUSES = ["sending", "send_unknown"];

/** An AUS deal, read the way the backend's `app_region` reads it. */
export function isAusDeal(application) {
  if (!application) return false;
  if ((application.region || "").toUpperCase() === "AUS") return true;
  if ((application.currency || "").toUpperCase() === "AUD") return true;
  return ["AUS", "AU", "AUSTRALIA"].includes((application.country || "").toUpperCase());
}

/**
 * True when the Admin's release would be refused whatever they enter, so the screen does
 * not offer it. Read per stored payment id, as `release_path` in
 * `services/disbursement_lock.py` reads it: a payment on both rails is an engineer's case; a
 * payment on one rail is checked with that rail, so the Admin is offered the release; and
 * with no payment recorded, an AUS deal whose send may have reached Capay is an engineer's
 * case, since a Capay payment may exist under the deal's reference and only an engineer can
 * check it. `RAIL_CALL_STATUSES` and the fields read here are held to the backend's by
 * `backend/tests/test_disbursement_lock_frontend_mirror.py`.
 */
export function releaseNeedsEngineer(application) {
  if (!application) return false;
  const gocardless = !!application.disbursement_gc_payment_id;
  const capay = !!application.disbursement_capay_payment_id;
  if (gocardless && capay) return true;
  if (gocardless || capay) return false;
  return (
    isAusDeal(application) &&
    RAIL_CALL_STATUSES.includes(application.disbursement_gc_send_status)
  );
}

/**
 * The note shown to an Admin in place of the Release lock button when `releaseNeedsEngineer`
 * is true: a payment on both rails, or no payment recorded on an AUS send that may have
 * reached Capay.
 */
export function releaseEngineerNote(application) {
  if (application?.disbursement_gc_payment_id && application?.disbursement_capay_payment_id) {
    return "This disbursement is locked and records both a GoCardless payment and a Capay payment. An engineer must check both before it can be released.";
  }
  return "This disbursement is locked and no payment is recorded. A Capay payment may exist for it, so an engineer must check Capay before it can be released.";
}

/**
 * The payment the deal records, with its rail, for the release dialog: "GoCardless OBP-1",
 * "Capay PAY-1", both joined with "and", or "None recorded".
 */
export function storedPaymentLabel(application) {
  const parts = [];
  if (application?.disbursement_gc_payment_id) {
    parts.push(`GoCardless ${application.disbursement_gc_payment_id}`);
  }
  if (application?.disbursement_capay_payment_id) {
    parts.push(`Capay ${application.disbursement_capay_payment_id}`);
  }
  return parts.length ? parts.join(" and ") : "None recorded";
}

/**
 * True when releasing needs the "no manual transfer was made" confirmation: the deal has
 * no payment recorded, on either rail, and its send was a manual one. With a payment
 * recorded the release reads that payment instead and asks for no confirmation.
 */
export function releaseNeedsManualConfirmation(application) {
  return (
    !!application &&
    MANUAL_TRANSFER_STATUSES.includes(application.disbursement_gc_send_status) &&
    !application.disbursement_gc_payment_id &&
    !application.disbursement_capay_payment_id
  );
}

const UK_UTC_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * A stored timestamp as `dd/mm/yyyy HH:MM UTC`, in UTC whatever the browser's zone, matching
 * the backend's `uk_utc`. A value with no offset is read as UTC, as the backend reads it.
 * Seconds are dropped, not rounded, so a limit read off the screen is never later than the
 * real one. Returns `fallback` for a missing or unreadable value.
 */
export function formatUkUtc(value, fallback = "not recorded") {
  if (!value) return fallback;
  const text = String(value);
  const naive = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text);
  const date = new Date(naive ? `${text}Z` : value);
  if (Number.isNaN(date.getTime())) return fallback;
  const part = Object.fromEntries(UK_UTC_PARTS.formatToParts(date).map((p) => [p.type, p.value]));
  return `${part.day}/${part.month}/${part.year} ${part.hour}:${part.minute} UTC`;
}

/**
 * The line shown to an Admin on a locked deal when releasing is not offered because the
 * release switch is off. An unreadable or damaged switch says so, rather than claiming it was
 * switched off; a switch that was turned off with a reason says when (UK order, UTC) and why.
 * It never names the person who switched it off.
 */
export function releaseSwitchOffNote(switchState) {
  const locked = "This disbursement is locked against a second send. ";
  if (switchState?.state === "unreadable") {
    return `${locked}Could not read the release switch; releasing stays unavailable until it can be read.`;
  }
  if (switchState?.state === "malformed") {
    return `${locked}The release switch record is damaged; releasing stays unavailable until an Admin switches it on again in Portal Settings, under Workflow & Disbursement.`;
  }
  const off = `${locked}Releasing a lock is switched off; it can be switched on in Portal Settings, under Workflow & Disbursement.`;
  if (switchState?.state === "disabled" && switchState.reason && switchState.changed_at) {
    return `${off} It was switched off on ${formatUkUtc(switchState.changed_at)}: ${switchState.reason}`;
  }
  return off;
}
